import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UserPlusIcon, MagnifyingGlassIcon, PencilIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { Modal, Pagination, Table, Button, TextInput, Tooltip, Tabs, Loader, PasswordInput } from '@mantine/core';
import MemberModal from './components/MemberModal';
import { useAdminStore } from '@/store/admin';
import { useTranslation } from 'react-i18next';
import { resetUserPassword, updateUser } from '@mygpt/app';
import { showNotification } from '@mantine/notifications';
import { useGPTStore } from '@/store';

export default function Employees({
  currentPage: initialPage,
  setCurrentPage,
}: {
  currentPage: number;
  setCurrentPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  const {
    companyId,
    userList,
    fetchUserList,
    fetchDepartmentByCompanyId,
    companySetting,
    refreshDepartmentData,
    userOgsId,
  } = useAdminStore((state) => state);
  const { userInfo, roles } = useGPTStore((state) => state);

  const [employees, setEmployees] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPageLocal] = useState(initialPage);
  const [isSearching, setIsSearching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [departments, setDepartments] = useState<any[]>([]);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<any | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastRequestRef = useRef<string>('');

  // 创建稳定的请求函数
  const fetchData = useCallback(() => {
    const params: any = { currentPage, pageSize: 10, companyId, enabled: null };
    if (currentSearchQuery.trim() !== '') {
      params.inCompanyUserNameInfixLike = currentSearchQuery;
    }

    // 生成请求的唯一标识，防止重复请求
    const requestKey = `${currentPage}-${currentSearchQuery}-${refreshTrigger}`;
    if (lastRequestRef.current === requestKey) {
      return;
    }

    lastRequestRef.current = requestKey;
    setTableLoading(true);
    fetchUserList(params).finally(() => setTableLoading(false));
  }, [currentPage, currentSearchQuery, companyId, fetchUserList, refreshTrigger]);

  // 获取当前用户的最高权限角色
  const getCurrentUserHighestRole = () => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return null;
    }

    const roleHierarchy = {
      ROLE_COMPANY_OWNER: 4,
      ROLE_ADMIN: 3,
      ROLE_EDITOR: 2,
      ROLE_VIEWER: 1,
    };

    return roles.reduce((highest, current) => {
      if (!current || !highest) {
        return current || highest;
      }

      const currentLevel = roleHierarchy[current.code as keyof typeof roleHierarchy] || 0;
      const highestLevel = roleHierarchy[highest.code as keyof typeof roleHierarchy] || 0;
      return currentLevel > highestLevel ? current : highest;
    });
  };

  const currentUserRole = getCurrentUserHighestRole();

  // 检查当前用户是否可以添加指定角色
  const canAddRole = (roleCode: string) => {
    if (!currentUserRole) return false;

    switch (currentUserRole.code) {
      case 'ROLE_COMPANY_OWNER':
        return true; // 公司所有者可以添加所有角色
      case 'ROLE_ADMIN':
        return roleCode === 'ROLE_EDITOR' || roleCode === 'ROLE_VIEWER'; // 管理员只能添加编辑者和查看者
      default:
        return false; // 其他角色不能添加用户
    }
  };

  // 检查当前用户是否可以操作指定用户
  const canOperateUser = (targetUser: any) => {
    if (!currentUserRole || !targetUser.roles || !Array.isArray(targetUser.roles) || targetUser.roles.length === 0) {
      return false;
    }

    // 不能操作自己（优先用OGS userId匹配，email可能带OAuth前缀）
    const isSelf = userOgsId ? String(targetUser.id) === userOgsId : userInfo?.email === targetUser.email;
    if (isSelf) {
      return false;
    }

    // 获取目标用户的最高权限角色
    const targetUserHighestRole = targetUser.roles.reduce((highest: any, current: any) => {
      if (!current || !highest) {
        return current || highest;
      }

      const roleHierarchy = {
        ROLE_COMPANY_OWNER: 4,
        ROLE_ADMIN: 3,
        ROLE_EDITOR: 2,
        ROLE_VIEWER: 1,
      };
      const currentLevel = roleHierarchy[current.code as keyof typeof roleHierarchy] || 0;
      const highestLevel = roleHierarchy[highest.code as keyof typeof roleHierarchy] || 0;
      return currentLevel > highestLevel ? current : highest;
    });

    switch (currentUserRole.code) {
      case 'ROLE_COMPANY_OWNER':
        return true; // 公司所有者可以操作所有用户
      case 'ROLE_ADMIN':
        // 管理员只能操作编辑者和查看者
        return targetUserHighestRole.code === 'ROLE_EDITOR' || targetUserHighestRole.code === 'ROLE_VIEWER';
      default:
        return false; // 其他角色不能操作用户
    }
  };

  // 检查当前用户是否可以添加新用户
  const canAddUser = () => {
    return currentUserRole && (currentUserRole.code === 'ROLE_COMPANY_OWNER' || currentUserRole.code === 'ROLE_ADMIN');
  };

  useEffect(() => {
    if (userList && userList.users) {
      setEmployees(userList.users);
      setTotalEmployees(userList.paginator.items);
    }
    if (userList && userList.paginator) {
      setTotalPages(userList.paginator.pages);
    }
    if (userList && userList.departments) {
      setDepartments(userList.departments);
    }
    console.log(userInfo);
  }, [userList, currentPage]);
  useEffect(() => {
    if (initialPage !== currentPage && !isSearching) {
      setCurrentPageLocal(initialPage);
    }
  }, [initialPage, currentPage, isSearching]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (page: number) => {
    setCurrentPageLocal(page);
    setCurrentPage(page);
    // fetchUserList({ currentPage: page, pageSize: 10, companyId });
  };

  const handleMemberSubmit = (formData: any) => {
    // 通过刷新触发器来刷新数据，保持搜索状态
    setRefreshTrigger((prev) => prev + 1);
    // 刷新部门数据，确保 LocalMemberSelector 显示最新的用户权限
    refreshDepartmentData();
    setShowMemberModal(false);
    setEditingUser(null);
  };

  const handleSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsSearching(true);
      React.startTransition(() => {
        setCurrentSearchQuery(searchQuery);
        setCurrentPageLocal(1);
        setCurrentPage(1); // 同步父组件状态
      });
      // 重置搜索状态
      setTimeout(() => setIsSearching(false), 100);
    }
  };

  const handleClearSearch = () => {
    setIsSearching(true);
    React.startTransition(() => {
      setSearchQuery('');
      setCurrentSearchQuery('');
      setCurrentPageLocal(1);
      setCurrentPage(1); // 同步父组件状态
    });
    // 重置搜索状态
    setTimeout(() => setIsSearching(false), 100);
  };

  const handleToggleUserStatus = (userId: string) => {
    if (operationLoading) return;

    setOperationLoading(userId);
    const user = employees.find((u) => u.id === userId);
    if (!user) return;

    const isEnabled = user.userCompanyEnabled;
    const updateData = {
      id: userId,
      name: user.name,
      email: user.email,
      enabled: !isEnabled,
      userCompanyEnabled: !isEnabled,
      sysRoleId: user.roles?.[0]?.id,
    };

    updateUser(updateData)
      .then(() => {
        // 通过刷新触发器来刷新数据，保持搜索状态
        setRefreshTrigger((prev) => prev + 1);
        // 刷新部门数据，确保 LocalMemberSelector 显示最新的用户权限
        refreshDepartmentData();
        showNotification({
          message: isEnabled
            ? t('adminConsole.employees.disableUserSuccess')
            : t('adminConsole.employees.enableUserSuccess'),
          color: 'green',
        });
      })
      .catch(() => {
        showNotification({
          message: isEnabled
            ? t('adminConsole.employees.disableUserFailed')
            : t('adminConsole.employees.enableUserFailed'),
          color: 'red',
        });
      })
      .finally(() => {
        setOperationLoading(null);
      });
  };

  const handleEditClick = (user: any) => {
    setEditingUser(user);
    setShowMemberModal(true);
  };

  const handleAddMemberClick = () => {
    setEditingUser(null);
    setShowMemberModal(true);
  };

  const handleResetPasswordClick = (user: any) => {
    setSelectedUserForReset(user);
    setNewPassword(companySetting?.initialPassword);
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordSubmit = () => {
    if (!selectedUserForReset || !newPassword.trim()) {
      showNotification({
        message: t('adminConsole.employees.passwordRequired'),
        color: 'red',
      });
      return;
    }

    setOperationLoading(`reset-${selectedUserForReset.id}`);
    resetUserPassword({ id: selectedUserForReset.id, password: newPassword })
      .then((res) => {
        if (res.data.success) {
          setShowResetPasswordModal(false);
          setSelectedUserForReset(null);
          setNewPassword('');
          showNotification({
            message: t('adminConsole.employees.resetPasswordSuccess'),
            color: 'green',
          });
        } else {
          showNotification({
            message: t('adminConsole.employees.resetPasswordFailed'),
            color: 'red',
          });
        }
      })
      .finally(() => {
        setOperationLoading(null);
      });
  };

  return (
    <div className="space-y-8 p-6 rounded-lg">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('adminConsole.employees.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('adminConsole.employees.description')}</p>
        </div>

        <div className="flex items-center gap-4">
          {canAddUser() ? (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddMemberClick}
              className="inline-flex items-center gap-2 px-4 py-2
                bg-white border border-gray-300 text-gray-700 rounded-lg
                hover:bg-gray-50 shadow-sm transition-colors"
            >
              <UserPlusIcon className="h-5 w-5" />
              <span>{t('adminConsole.employees.addEmployee')}</span>
            </motion.button>
          ) : (
            <Tooltip label={t('adminConsole.employees.noPermissionToAdd')} withArrow>
              <motion.button
                disabled
                className="inline-flex items-center gap-2 px-4 py-2
                  bg-gray-100 border border-gray-200 text-gray-400 rounded-lg
                  cursor-not-allowed opacity-50"
              >
                <UserPlusIcon className="h-5 w-5" />
                <span>{t('adminConsole.employees.addEmployee')}</span>
              </motion.button>
            </Tooltip>
          )}
        </div>
      </div>
      {!loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-sm">
                <span className="text-gray-600">{t('adminConsole.employees.totalMembers')}</span>
                <p className="mt-1 text-lg font-semibold text-gray-900">{totalEmployees}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <TextInput
                  leftSection={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
                  placeholder={t('adminConsole.employees.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearch}
                  className="w-[320px]"
                />
                {currentSearchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title={t('common.clear')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {tableLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="text-center">
              <Loader size="lg" variant="dots" />
              <p className="mt-4 text-gray-500">{t('adminConsole.employees.loadingData')}</p>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            <Table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                    {t('adminConsole.employees.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                    {t('adminConsole.employees.department')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                    {t('adminConsole.memberModal.role')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                    {t('adminConsole.employees.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                    {t('adminConsole.employees.createdAt')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 sticky right-0 bg-gray-100">
                    {t('adminConsole.employees.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Tooltip label={employee.name} withArrow>
                        <div
                          className="font-medium text-gray-900"
                          style={{
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {employee.name}
                        </div>
                      </Tooltip>
                      <Tooltip label={employee.email} withArrow>
                        <div
                          className="text-xs text-gray-400 mt-1"
                          style={{
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {employee.email}
                        </div>
                      </Tooltip>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <Tooltip
                        label={
                          employee.departmentIds && employee.departmentIds.length > 0
                            ? employee.departmentIds
                                .map((deptId: string) => {
                                  const department = departments.find((d) => d.id === deptId);
                                  return department ? department.name : '';
                                })
                                .join(', ')
                            : ''
                        }
                        withArrow
                      >
                        <div
                          style={{
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {employee.departmentIds && employee.departmentIds.length > 0
                            ? employee.departmentIds
                                .map((deptId: string) => {
                                  const department = departments.find((d) => d.id === deptId);
                                  return department ? department.name : '';
                                })
                                .join(', ')
                            : ''}
                        </div>
                      </Tooltip>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {Array.isArray(employee.roles) && employee.roles.length > 0 ? (
                        employee.roles.map((role: any, idx: number) => {
                          // 根据角色代码获取多语言显示名称
                          const roleDisplayName = role.code
                            ? t(`adminConsole.employees.roles.${role.code}`)
                            : role.name;
                          let bg = '',
                            text = '';
                          if (role.code === 'ROLE_COMPANY_OWNER') {
                            bg = 'bg-red-100';
                            text = 'text-red-700';
                          } else if (role.code === 'ROLE_ADMIN') {
                            bg = 'bg-orange-100';
                            text = 'text-orange-700';
                          } else if (role.code === 'ROLE_EDITOR') {
                            bg = 'bg-blue-100';
                            text = 'text-blue-700';
                          } else if (role.code === 'ROLE_VIEWER') {
                            bg = 'bg-green-100';
                            text = 'text-green-700';
                          } else {
                            bg = 'bg-gray-100';
                            text = 'text-gray-700';
                          }
                          return (
                            <span
                              key={role.code + idx}
                              className={`inline-block px-2 py-0.5 rounded-full font-bold ${bg} ${text} mb-1`}
                              style={{ fontSize: '13px', letterSpacing: '0.5px' }}
                            >
                              {roleDisplayName}
                            </span>
                          );
                        })
                      ) : (
                        <span
                          className="inline-block px-4 py-1 rounded-full font-bold bg-gray-100 text-gray-700"
                          style={{ fontSize: '16px', letterSpacing: '1px' }}
                        >
                          -
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          employee.userCompanyEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {employee.userCompanyEnabled
                          ? t('adminConsole.employees.enabled')
                          : t('adminConsole.employees.disabled')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{employee.createdAt}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 sticky right-0 bg-white">
                      {(userOgsId ? String(employee.id) !== userOgsId : userInfo?.email !== employee.email) ? (
                        canOperateUser(employee) ? (
                          <>
                            <Tooltip label={t('adminConsole.employees.editTooltip')} withArrow>
                              <Button
                                variant="subtle"
                                size="xs"
                                className="mr-2"
                                onClick={() => handleEditClick(employee)}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip
                              label={
                                employee.userCompanyEnabled
                                  ? t('adminConsole.employees.disableUser')
                                  : t('adminConsole.employees.enableUser')
                              }
                              withArrow
                            >
                              <Button
                                variant="subtle"
                                size="xs"
                                className="mr-2"
                                onClick={() => handleToggleUserStatus(employee.id)}
                                disabled={operationLoading === employee.id}
                                leftSection={operationLoading === employee.id ? <Loader size="xs" /> : null}
                              >
                                {operationLoading === employee.id ? (
                                  t('common.processing')
                                ) : employee.userCompanyEnabled ? (
                                  <PauseIcon className="h-4 w-4" />
                                ) : (
                                  <PlayIcon className="h-4 w-4" />
                                )}
                              </Button>
                            </Tooltip>
                            <Tooltip label={t('adminConsole.employees.resetPasswordTooltip')} withArrow>
                              <Button
                                variant="subtle"
                                size="xs"
                                onClick={() => handleResetPasswordClick(employee)}
                                disabled={operationLoading === `reset-${employee.id}`}
                                leftSection={operationLoading === `reset-${employee.id}` ? <Loader size="xs" /> : null}
                              >
                                {operationLoading === `reset-${employee.id}` ? t('common.processing') : <span>🔑</span>}
                              </Button>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip label={t('adminConsole.employees.noPermissionToEdit')} withArrow>
                              <Button
                                variant="subtle"
                                size="xs"
                                className="mr-2 opacity-50 cursor-not-allowed"
                                disabled
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip label={t('adminConsole.employees.noPermissionToToggle')} withArrow>
                              <Button
                                variant="subtle"
                                size="xs"
                                className="mr-2 opacity-50 cursor-not-allowed"
                                disabled
                              >
                                {employee.userCompanyEnabled ? (
                                  <PauseIcon className="h-4 w-4" />
                                ) : (
                                  <PlayIcon className="h-4 w-4" />
                                )}
                              </Button>
                            </Tooltip>
                            <Tooltip label={t('adminConsole.employees.noPermissionToReset')} withArrow>
                              <Button variant="subtle" size="xs" className="opacity-50 cursor-not-allowed" disabled>
                                <span>🔑</span>
                              </Button>
                            </Tooltip>
                          </>
                        )
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      <div className="flex justify-center my-4">
        <Pagination value={currentPage} onChange={handlePageChange} total={totalPages} />
      </div>
      <Modal
        opened={showMemberModal}
        onClose={() => setShowMemberModal(false)}
        title={editingUser ? t('adminConsole.employees.editMember') : t('adminConsole.employees.addMember')}
        centered
      >
        <MemberModal
          onSubmit={handleMemberSubmit}
          onClose={() => setShowMemberModal(false)}
          userId={editingUser?.id}
          companyId={companyId}
          initialData={editingUser}
        />
      </Modal>

      <Modal
        opened={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        title={t('adminConsole.employees.resetPasswordTooltip')}
        centered
      >
        <div>
          <PasswordInput
            label={t('adminConsole.employees.newPassword')}
            placeholder={t('adminConsole.employees.newPasswordPlaceholder')}
            value={newPassword}
            onChange={(e) => {
              // 过滤掉汉字，只允许英文字母、数字和特殊字符
              const filteredValue = e.target.value.replace(/[\u4e00-\u9fa5]/g, '');
              setNewPassword(filteredValue);
            }}
            required
            error={newPassword.length < 6 ? t('adminConsole.employees.passwordTooShort') : ''}
          />
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => setShowResetPasswordModal(false)}
              className="mr-2"
              disabled={!!operationLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleResetPasswordSubmit}
              disabled={!!operationLoading}
              leftSection={operationLoading ? <Loader size="xs" /> : null}
            >
              {operationLoading ? t('common.submitting') : t('common.saveChanges')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
